import { useContext } from 'react';
import { Action, Subject } from '@erp/authz';
import { AuthContext } from '@/context/auth.context';
import { useCan } from '@/hooks/useCan';
import {
  useTaskPageCounts,
  useTaskPages,
} from '@/api/rpc/hooks/tasks/useTaskPages';
import { taskPageNavIcon } from '@/components/task-pages/taskPageIcons';
import { TaskPageCountBadge } from '@/components/sidenav/TaskPageCountBadge';
import {
  taskPageNavEntry,
  type TaskPageNavItem,
} from '@/routing/task-pages-nav';

/**
 * The current user's visible task pages as sidebar nav items. Empty while
 * loading, when the org has no pages, or when the user lacks Read on
 * TaskPage (the query is never fired then, and a forbidden/errored response
 * also renders no pages section).
 *
 * Each item carries a badge with the user's open tasks on that page (or a
 * marker when only others have open tasks) once the counts query resolves.
 * Counts need Read on Task as well; pages render without badges when that is
 * missing — including any cached counts from before the permission changed.
 */
export function useTaskPageNavItems(): TaskPageNavItem[] {
  const { currentUser } = useContext(AuthContext);
  const [canReadTaskPages] = useCan(Action.Read, Subject.TaskPage);
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  const enabled = canReadTaskPages;
  const { data: taskPages } = useTaskPages({ enabled });
  const { data: taskPageCounts } = useTaskPageCounts({
    enabled: enabled && canReadTasks && (taskPages?.length ?? 0) > 0,
  });
  const counts = canReadTasks ? taskPageCounts?.counts : undefined;

  if (!enabled || !taskPages) return [];
  return taskPages.map((page) => {
    const count = counts?.[page.id];
    return {
      entry: taskPageNavEntry({
        page,
        icon: taskPageNavIcon(page.icon),
        badge: count ? (
          <TaskPageCountBadge count={count} currentUserId={currentUser?.id} />
        ) : null,
      }),
      navGroup: page.navGroup,
      pageSectionId: page.pageSectionId,
      sortOrder: page.sortOrder,
    };
  });
}
